import uniqueId from 'lodash-es/uniqueId';
import React from 'react';
import { TTab, TTabsListState, TabsControllerProps } from './types';
import { getLabel, getSpecificParent, isChild } from '@apia/util';
import { ApiaUtil } from '../objects/ApiaUtil';
import { makeAutoObservable, observable } from 'mobx';
import axios from 'axios';

export class Tab<TabType extends TTab = TTab> {
  constructor(
    public state: TabType,
    public controller: TabsController<TabType>,
  ) {
    makeAutoObservable(this, { state: observable });
  }

  get id() {
    return this.state.id;
  }

  /**
   * El parámetro force evita que se muestre cartel de confirmación al usuario cuando puede haber pérdida de datos.
   */
  async close(force?: boolean) {
    return this.controller.closeTab(this.state.id, force);
  }

  open(closeOthers?: boolean, force?: boolean) {
    if (!this.state.isOpen || force) {
      this.controller.openTab(this.id, closeOthers);
    } else {
      const tab = this.controller.getTab(this.id);
      if (tab && this.state.onFocus) {
        this.state.onFocus(tab);
      }
    }
  }

  remove() {
    this.controller.remove(this.id);
  }

  toggle(isShown?: boolean) {
    this.controller.toggleTab(this.state.id, isShown);
  }
}

export type TAppendBehavior = Partial<{
  closeOthers: boolean;
  insertAfterId: string;
}>;

export default class TabsController<TabType extends TTab = TTab> {
  state: TTabsListState<TabType> = {
    isMultiple: false,
    openTabsHistory: new Set(),
    tabs: new Map(),
    timestamp: 0,
    showAtLeastOneTab: false,
    listenFocusEvents: true,
    allowDetach: false,
  };

  getActiveTab() {
    return this.activeTab;
  }
  /**
   * Es el tab que está abierto
   */
  get activeTab() {
    const tabs = [...this.state.openTabsHistory];

    return tabs[tabs.length - 1];
  }

  getTab(id: string) {
    return this.state.tabs.get(id);
  }

  get tabsList() {
    return [...this.state.tabs.values()];
  }

  constructor(
    public id: string,
    public props: Omit<TabsControllerProps<TabType>, 'id'>,
  ) {
    Object.assign(this.state, props.initialState ?? {});

    props.initialTabs?.forEach((tab) => this.append(tab));
    if (props.initialTabs && props.initialTabs.length > 0)
      this.focusTab(props.initialTabs[0].id);

    makeAutoObservable(this);
  }

  #innerAppend(tab: TabType, behavior?: TAppendBehavior) {
    const tabsArray = [...this.state.tabs.values()];

    const afterId =
      behavior?.insertAfterId ?? tabsArray[tabsArray.length - 1]?.id;
    for (let i = 0; i <= tabsArray.length; i++) {
      if (!tabsArray[i] || tabsArray[i]?.id === afterId) {
        tabsArray.splice(i + 1, 0, new Tab(tab, this));
        break;
      }
    }

    this.state.tabs = new Map(tabsArray.map((c) => [c.id, c]));

    if (tab.isOpen) {
      this.openTab(tab.id, behavior?.closeOthers);
      this.focusTab(tab.id, behavior?.closeOthers);
    }

    this.checkAtLeastOneOpen();
  }

  /**
   * Obviamente agrega un tab al listado de tabs
   */
  append(tab: TabType, behavior?: TAppendBehavior) {
    if (this.getTab(tab.id)) {
      void ApiaUtil.instance.dialogs
        .confirm({
          children: getLabel('msgTabIsOpen').text,
          title: getLabel('titMsgWarning').text,
        })
        .then((hasConfirmed) => {
          if (hasConfirmed)
            this.#innerAppend(
              {
                ...tab,
                id: `${tab.id}_${uniqueId()}`,
                additionalProps: tab.additionalProps ?? {},
              } as TabType,
              behavior,
            );
        });
    } else this.#innerAppend(tab, behavior);
  }

  async closeAll(closeFixedTabsAsWell = false, force = false) {
    for (const tab of this.tabsList) {
      if (!tab.state.isFixed || closeFixedTabsAsWell) {
        tab.close(force);
      }
    }
  }
  async openAll() {
    for (const tab of this.tabsList) {
      if (!tab.state.isFixed) {
        tab.open(false);
      }
    }
  }

  closeOpenTab(force?: boolean) {
    const openTab = this.activeTab;
    openTab?.close(force);
  }

  async closeToRight(targetId: string, force?: boolean) {
    const tabsList = this.tabsList;
    const startIndex = tabsList.findIndex((c) => c.state.id === targetId);

    if (startIndex >= 0) {
      for (let i = startIndex + 1; i < tabsList.length; i++) {
        tabsList[i].close(force);
      }
    }
  }

  async closeToLeft(targetId: string, force?: boolean) {
    const tabsList = this.tabsList;
    const startIndex = tabsList.findIndex((c) => c.state.id === targetId);

    if (startIndex >= 0) {
      for (let i = startIndex - 1; i >= 0; i--) {
        await tabsList[i].close(force);
      }
    }
  }

  async closeOthers(targetId: string, force?: boolean) {
    for await (const tab of this.tabsList) {
      if (tab.state.id !== targetId) {
        await tab.close(force);
      }
    }
  }

  async closeTab(tabId: string, force = false) {
    const tab = this.getTab(tabId);
    const tabProps = tab?.state;

    if (tab && tabProps) {
      if (!tabProps.isClosable && !force) return false;

      const { onBeforeClose } = tabProps;
      if (onBeforeClose) {
        const canClose = await onBeforeClose();
        const message =
          canClose === false ? getLabel('msgPerDatIng').text : canClose;
        if (message !== true && !force) {
          const hasConfirmed = await ApiaUtil.instance.dialogs.confirm({
            children: message,
            title: getLabel('lbl_atention').text,
          });
          if (!hasConfirmed) return false;
          /*else {
            await axios.post(
              window.CONTEXT +
                '/apia.splash.MenuAction.run?action=removeTab&isAjax=true&tabId=' +
                tabId +
                window.TAB_ID_REQUEST,
            );
          }*/
        }
      }

      this.state.openTabsHistory.delete(tab);
      this.getActiveTab()?.open();
      this.state.tabs.delete(tab.id);

      await axios.post(
              window.CONTEXT +
                '/apia.splash.MenuAction.run?action=removeTab&isAjax=true&tabId=' +
                tabId +
                window.TAB_ID_REQUEST,
            );

      this.props?.onCloseTab?.(tab);
    }
    return true;
  }

  private checkAtLeastOneOpen() {
    if (
      this.state.showAtLeastOneTab &&
      !this.tabsList.find((c) => c.state.isOpen)
    ) {
      this.openTab(this.tabsList[0].id);
    }
  }

  remove(tabId: string) {
    this.state.openTabsHistory.delete(this.state.tabs.get(tabId)!);
    this.state.tabs.delete(tabId);
    if (this.state.openTabsHistory.size === 0 && this.state.showAtLeastOneTab) {
      this.tabsList[0]?.open();
    }
  }

  async toggleTab(tabId: string, isShown?: boolean) {
    const willBeOpen = isShown || !this.getTab(tabId)?.state.isOpen;

    if (willBeOpen) {
      this.openTab(tabId, false);
    } else {
      if (
        !this.state.showAtLeastOneTab ||
        this.tabsList.find((c) => c.state.isOpen && c.id !== tabId)
      )
        this.getTab(tabId)!.state.isOpen = false;
    }
  }

  focusNextTab() {
    const tabsList = this.tabsList;
    const focusedIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (focusedIndex === tabsList.length - 1 || focusedIndex === -1)
      return null;

    const newFocusIndex: number = focusedIndex + 1;
    this.focusTab(tabsList[newFocusIndex].id);

    return tabsList[newFocusIndex];
  }

  focusPreviousTab() {
    const tabsList = this.tabsList;
    const focusedIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (focusedIndex <= 0) return null;

    const newFocusIndex: number = focusedIndex - 1;
    this.focusTab(tabsList[newFocusIndex].id);

    return tabsList[newFocusIndex];
  }

  focusTab(tabId: string, closeOthers?: boolean) {
    this.openTab(tabId, closeOthers);
  }

  getTabElement(tabId: string) {
    return document.querySelector(`#tabpanel-${this.id}-${tabId}`);
  }

  handleKeyDown(ev: React.KeyboardEvent) {
    if (
      !isChild(
        ev.target as HTMLElement,
        (current) => current.id === `tabsList-${this.id}`,
      )
    )
      return;

    switch (ev.code) {
      case 'ArrowDown':
      case 'ArrowRight': {
        if (
          (this.props?.orientation === 'horizontal' &&
            ev.code === 'ArrowDown') ||
          (this.props?.orientation === 'vertical' && ev.code === 'ArrowRight')
        )
          return;

        this.focusNextTab();

        break;
      }
      case 'ArrowUp':
      case 'ArrowLeft': {
        if (
          (this.props.orientation === 'horizontal' && ev.code === 'ArrowUp') ||
          (this.props.orientation === 'vertical' && ev.code === 'ArrowLeft')
        )
          return;

        this.focusPreviousTab();

        break;
      }
      case 'Delete': {
        const tabId = getSpecificParent(
          ev.target as HTMLElement,
          (current) => !!current.getAttribute('data-id'),
        )?.getAttribute('data-id');

        if (tabId) {
          if (!this.getTab(tabId)?.state.isClosable) return;

          void this.closeTab(tabId);
        }
        break;
      }
      case 'Enter':
      case 'Space': {
        const tabId = getSpecificParent(
          ev.target as HTMLElement,
          (current) => !!current.getAttribute('data-id'),
        )?.getAttribute('data-id');

        if (tabId) {
          this.openTab(tabId);
        }

        break;
      }
      case 'End': {
        this.openTab(this.tabsList[this.tabsList.length - 1].id);
        break;
      }
      case 'Home': {
        this.focusTab(this.tabsList[0].id);
        break;
      }
      default: {
        break;
      }
    }
  }

  openNextTab() {
    const tabsList = this.tabsList;
    const openIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (openIndex == tabsList.length - 1) return null;

    const newOpenIndex: number = openIndex + 1;
    this.openTab(tabsList[newOpenIndex].id);

    return tabsList[newOpenIndex];
  }

  openPreviousTab() {
    const tabsList = this.tabsList;
    const openIndex = tabsList.findIndex(
      (current) => current === this.activeTab,
    );

    if (openIndex <= 0) return null;

    const newOpenIndex: number = openIndex - 1;
    this.openTab(tabsList[newOpenIndex].id);

    return tabsList[newOpenIndex];
  }

  /**
   * Se utiliza para hacer que el tab aparezca visible en el listado
   */
  openTab(tabId: string, closeOthers?: boolean) {
    const tab = this.getTab(tabId);
    if (!tab) {
      console.warn(`There is no tab with such id: ${tabId}`);
    } else {
      try {
        if (closeOthers !== false) {
          this.state.tabs.forEach((c) => {
            c.state.isOpen = false;
          });
        }
      } catch (e) {
        e; // No hay ningún tab abierto, no me importa
      }
      tab.state.isOpen = true;
      tab.state.isFocused = true;
      document
        .querySelector<HTMLElement>(`.tabs__item__wrapper[data-id="${tab.id}"]`)
        ?.focus();
      tab.state.onFocus?.(tab);
      if (this.state.openTabsHistory.has(tab)) {
        this.state.openTabsHistory.delete(tab);
      }
      this.state.openTabsHistory.add(tab);
      this.checkAtLeastOneOpen();
    }
  }
}
